import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '../../components/common'
import { ImageUpload } from '../../components/admin'
import { MapPicker } from '../../components/map'
import { Locale, TipoLocale, Provincia, FasciaPrezzo } from '../../types'
import api from '../../services/api'

const TIPI_LOCALE: TipoLocale[] = ['ristorante', 'trattoria', 'agriturismo', 'osteria', 'pizzeria', 'pub']
const PROVINCE: Provincia[] = ['AN', 'PU', 'MC', 'FM', 'AP']
const FASCE_PREZZO: FasciaPrezzo[] = ['€', '€€', '€€€']

/**
 * Form creazione/modifica locale
 */
// Interfaccia dati segnalazione per pre-compilazione
interface SegnalazioneData {
  id: number
  nomeLocale: string
  tipoLocale?: string
  indirizzoApprossimativo?: string
  comune?: string
  provincia?: string
  descrizione?: string
  specialitaSuggerite?: string
  comeScoperto?: string
}

export default function AdminLocaleEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isNew = !id
  const segnalazioneId = searchParams.get('segnalazioneId')

  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [segnalazione, setSegnalazione] = useState<SegnalazioneData | null>(null)

  const [formData, setFormData] = useState({
    nome: '',
    slug: '',
    tipo: 'ristorante' as TipoLocale,
    indirizzo: '',
    comune: '',
    provincia: 'AN' as Provincia,
    cap: '',
    // Coordinate default: centro Marche (Ancona)
    latitudine: '43.6158',
    longitudine: '13.5189',
    telefono: '',
    email: '',
    sitoWeb: '',
    facebook: '',
    instagram: '',
    fasciaPrezzo: '€€' as FasciaPrezzo,
    specialita: '',
    descrizione: '',
    percheApprovato: '',
    parcheggioGrande: false,
    inEvidenza: false,
    attivo: true,
    fotoPrincipale: '',
    galleria: [] as string[],
  })

  useEffect(() => {
    if (!isNew && id) {
      fetchLocale(parseInt(id))
    } else if (isNew && segnalazioneId) {
      // Pre-compila il form con i dati della segnalazione
      fetchSegnalazione(parseInt(segnalazioneId))
    }
  }, [id, isNew, segnalazioneId])

  const fetchSegnalazione = async (segId: number) => {
    setLoading(true)
    try {
      const segnalazioni = await api.get<SegnalazioneData[]>('/admin/segnalazioni')
      const seg = (segnalazioni ?? []).find(s => s.id === segId)
      if (seg) {
        setSegnalazione(seg)
        // Pre-compila i campi disponibili dalla segnalazione
        const tipoValido = TIPI_LOCALE.includes(seg.tipoLocale as TipoLocale)
        const provinciaValida = PROVINCE.includes(seg.provincia as Provincia)
        setFormData(prev => ({
          ...prev,
          nome: seg.nomeLocale || '',
          slug: generateSlug(seg.nomeLocale || ''),
          tipo: tipoValido ? (seg.tipoLocale as TipoLocale) : 'ristorante',
          indirizzo: seg.indirizzoApprossimativo || '',
          comune: seg.comune || '',
          provincia: provinciaValida ? (seg.provincia as Provincia) : 'AN',
          descrizione: seg.descrizione || '',
          specialita: seg.specialitaSuggerite || '',
          // Usa "come scoperto" come base per "perché approvato"
          percheApprovato: seg.comeScoperto ? `Scoperto: ${seg.comeScoperto}` : '',
        }))
      } else {
        setError('Segnalazione non trovata')
      }
    } catch (error) {
      console.error('Errore caricamento segnalazione:', error)
      setError('Errore nel caricamento della segnalazione')
    } finally {
      setLoading(false)
    }
  }

  const fetchLocale = async (localeId: number) => {
    try {
      // L'API admin restituisce un array diretto
      const locali = await api.get<Locale[]>('/admin/locali')
      const locale = (locali ?? []).find(l => l.id === localeId)
      if (locale) {
        setFormData({
          nome: locale.nome,
          slug: locale.slug,
          tipo: locale.tipo,
          indirizzo: locale.indirizzo,
          comune: locale.comune,
          provincia: locale.provincia,
          cap: locale.cap || '',
          latitudine: String(locale.latitudine),
          longitudine: String(locale.longitudine),
          telefono: locale.telefono || '',
          email: locale.email || '',
          sitoWeb: locale.sitoWeb || '',
          facebook: locale.facebook || '',
          instagram: locale.instagram || '',
          fasciaPrezzo: locale.fasciaPrezzo || '€€',
          specialita: (locale.specialita || []).join(', '),
          descrizione: locale.descrizione || '',
          percheApprovato: locale.percheApprovato || '',
          parcheggioGrande: locale.parcheggioGrande,
          inEvidenza: locale.inEvidenza,
          attivo: locale.attivo,
          fotoPrincipale: locale.fotoPrincipale || '',
          galleria: locale.galleria || [],
        })
      }
    } catch (error) {
      console.error('Errore caricamento locale:', error)
      setError('Errore nel caricamento del locale')
    } finally {
      setLoading(false)
    }
  }

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value

    setFormData(prev => {
      const updated = { ...prev, [name]: newValue }
      // Auto-genera slug dal nome
      if (name === 'nome' && isNew) {
        updated.slug = generateSlug(value)
      }
      return updated
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (saving) return
    setError('')

    // Validazione coordinate
    const lat = parseFloat(formData.latitudine)
    const lng = parseFloat(formData.longitudine)
    if (isNaN(lat) || isNaN(lng)) {
      setError('Inserisci coordinate valide. Usa la mappa per selezionare la posizione.')
      return
    }

    setSaving(true)

    try {
      const payload = {
        nome: formData.nome,
        slug: formData.slug,
        tipo: formData.tipo,
        indirizzo: formData.indirizzo,
        comune: formData.comune,
        provincia: formData.provincia,
        latitudine: lat,
        longitudine: lng,
        fasciaPrezzo: formData.fasciaPrezzo,
        specialita: formData.specialita.split(',').map(s => s.trim()).filter(Boolean),
        cap: formData.cap || null,
        email: formData.email || null,
        telefono: formData.telefono || null,
        sitoWeb: formData.sitoWeb || null,
        facebook: formData.facebook || null,
        instagram: formData.instagram || null,
        descrizione: formData.descrizione || null,
        percheApprovato: formData.percheApprovato || null,
        parcheggioGrande: formData.parcheggioGrande,
        inEvidenza: formData.inEvidenza,
        attivo: formData.attivo,
        // Includi sempre le immagini per permettere la rimozione dalla galleria
        fotoPrincipale: formData.fotoPrincipale || null,
        galleria: formData.galleria,
      }

      // Debug: log payload
      console.log('Payload salvataggio:', JSON.stringify(payload, null, 2))

      if (segnalazioneId) {
        // Approva segnalazione e crea locale in un'unica operazione
        await api.post(`/admin/segnalazioni/${segnalazioneId}/approva`, payload)
      } else if (isNew) {
        await api.post('/admin/locali', payload)
      } else {
        await api.patch(`/admin/locali/${id}`, payload)
      }

      // Torna alla lista segnalazioni se venivamo da lì, altrimenti ai locali
      navigate(segnalazioneId ? '/admin/segnalazioni' : '/admin/locali')
    } catch (err: any) {
      setError(err.message || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-10 w-64 bg-gray-700 rounded"></div>
        <div className="h-96 bg-gray-700 rounded-xl"></div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Banner segnalazione */}
      {segnalazione && (
        <div className="bg-gold/10 border border-gold/30 text-gold px-4 py-3 rounded-lg">
          <p className="font-medium">Creazione locale da segnalazione</p>
          <p className="text-sm text-gold/80 mt-1">
            Completa i dati mancanti e salva per approvare la segnalazione e creare il locale.
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Informazioni base */}
      <div className="bg-night-light border border-gray-800 rounded-xl p-6">
        <h2 className="font-display text-xl text-white mb-6">Informazioni Base</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Nome *</label>
            <input
              type="text"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Slug</label>
            <input
              type="text"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Tipo *</label>
            <select
              name="tipo"
              value={formData.tipo}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
            >
              {TIPI_LOCALE.map(tipo => (
                <option key={tipo} value={tipo} className="capitalize">{tipo}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Fascia Prezzo</label>
            <select
              name="fasciaPrezzo"
              value={formData.fasciaPrezzo}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
            >
              {FASCE_PREZZO.map(fascia => (
                <option key={fascia} value={fascia}>{fascia}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Indirizzo */}
      <div className="bg-night-light border border-gray-800 rounded-xl p-6">
        <h2 className="font-display text-xl text-white mb-6">Indirizzo</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">Indirizzo *</label>
            <input
              type="text"
              name="indirizzo"
              value={formData.indirizzo}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Comune *</label>
            <input
              type="text"
              name="comune"
              value={formData.comune}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Provincia *</label>
            <select
              name="provincia"
              value={formData.provincia}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
            >
              {PROVINCE.map(prov => (
                <option key={prov} value={prov}>{prov}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">CAP</label>
            <input
              type="text"
              name="cap"
              value={formData.cap}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Latitudine *</label>
            <input
              type="number"
              step="any"
              name="latitudine"
              value={formData.latitudine}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Longitudine *</label>
            <input
              type="number"
              step="any"
              name="longitudine"
              value={formData.longitudine}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
              required
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">Seleziona sulla mappa</label>
            <MapPicker
              latitude={formData.latitudine ? parseFloat(formData.latitudine) : undefined}
              longitude={formData.longitudine ? parseFloat(formData.longitudine) : undefined}
              onChange={(lat, lng) => {
                setFormData(prev => ({
                  ...prev,
                  latitudine: lat.toFixed(6),
                  longitudine: lng.toFixed(6)
                }))
              }}
            />
          </div>
        </div>
      </div>

      {/* Contatti */}
      <div className="bg-night-light border border-gray-800 rounded-xl p-6">
        <h2 className="font-display text-xl text-white mb-6">Contatti</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Telefono</label>
            <input
              type="tel"
              name="telefono"
              value={formData.telefono}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Sito Web</label>
            <input
              type="url"
              name="sitoWeb"
              value={formData.sitoWeb}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Instagram</label>
            <input
              type="text"
              name="instagram"
              value={formData.instagram}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
              placeholder="@username"
            />
          </div>
        </div>
      </div>

      {/* Descrizione */}
      <div className="bg-night-light border border-gray-800 rounded-xl p-6">
        <h2 className="font-display text-xl text-white mb-6">Descrizione</h2>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Specialità (separate da virgola)
            </label>
            <input
              type="text"
              name="specialita"
              value={formData.specialita}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold"
              placeholder="Vincisgrassi, Brodetto, Olive ascolane"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Descrizione</label>
            <textarea
              name="descrizione"
              value={formData.descrizione}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Perché Approvato</label>
            <textarea
              name="percheApprovato"
              value={formData.percheApprovato}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-3 bg-night border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gold resize-none"
              placeholder="La storia di come il club ha scoperto questo locale..."
            />
          </div>
        </div>
      </div>

      {/* Immagini - solo per locali esistenti */}
      {!isNew && id && (
        <div className="bg-night-light border border-gray-800 rounded-xl p-6">
          <h2 className="font-display text-xl text-white mb-6">Immagini</h2>
          <ImageUpload
            localeId={parseInt(id)}
            currentImage={formData.fotoPrincipale}
            galleria={formData.galleria}
            onImageUploaded={(url, type) => {
              if (type === 'principale') {
                setFormData(prev => ({ ...prev, fotoPrincipale: url }))
              }
            }}
            onGalleryUpdated={(urls) => {
              setFormData(prev => ({ ...prev, galleria: urls }))
            }}
          />
        </div>
      )}

      {isNew && (
        <div className="bg-night-light border border-gray-800 rounded-xl p-6">
          <h2 className="font-display text-xl text-white mb-6">Immagini</h2>
          <p className="text-gray-400 text-sm">
            Potrai caricare le immagini dopo aver creato il locale.
          </p>
        </div>
      )}

      {/* Opzioni */}
      <div className="bg-night-light border border-gray-800 rounded-xl p-6">
        <h2 className="font-display text-xl text-white mb-6">Opzioni</h2>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="parcheggioGrande"
              checked={formData.parcheggioGrande}
              onChange={handleChange}
              className="w-5 h-5 rounded border-gray-600 bg-night text-gold focus:ring-gold"
            />
            <span className="text-gray-300">Parcheggio Grande</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="inEvidenza"
              checked={formData.inEvidenza}
              onChange={handleChange}
              className="w-5 h-5 rounded border-gray-600 bg-night text-gold focus:ring-gold"
            />
            <span className="text-gray-300">In Evidenza</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="attivo"
              checked={formData.attivo}
              onChange={handleChange}
              className="w-5 h-5 rounded border-gray-600 bg-night text-gold focus:ring-gold"
            />
            <span className="text-gray-300">Attivo (visibile sul sito)</span>
          </label>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-4">
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate(segnalazioneId ? '/admin/segnalazioni' : '/admin/locali')}
        >
          Annulla
        </Button>
        <Button type="submit" variant="primary" disabled={saving}>
          {saving
            ? 'Salvataggio...'
            : segnalazioneId
              ? 'Approva e Crea Locale'
              : isNew
                ? 'Crea Locale'
                : 'Salva Modifiche'}
        </Button>
      </div>
    </form>
  )
}
